import { forwardRef, useEffect } from "react";
import { useParams } from "react-router-dom";
import { PROXY_TYPES } from "../../../../../utils/constants";
import { Divider } from "../../../../../components/ui/Divider/Divider";
import { CfCaptcha } from "../../../../../components/common/CfCaptcha/CfCaptcha";
import { AgreeWithRules } from "../../../../../components/common/agreeWithRules/AgreeWithRules";
import { Button } from "../../../../../components/ui/Buttons/Button/Button";
import { calculatePrice } from "../../../../../utils/helpers";
import { getCountryFlag } from "../../../../../utils/helpers/getCountryFlag";
import { useTranslation } from "../../../../../hooks";
import { ButtonLoader } from "../../../../../components/ui/ButtonLoader/ButtonLoader";

export const Preview = forwardRef(({ price, setPrice, methods, setCfToken, setIsAgreeWithConditions, isLoading }, ref) => {
  const { lang } = useParams();

  const {
    proxyType,
    country,
    rent,
    asyncGoals,
    auth,
    mobileOperator,
    modemRotationMin,
    payment,
    quantity
  } = methods.watch();

  const {
    cabinet: { newOrder: {
      preview, proxyTypeLabel, location, priceByOne, quantityLabel, period, total, confirmOrder, operator, rotation
    } }
  } = useTranslation();

  const isEnoughDataForPriceCalculation = () => {
    const baseConditions = country?.id && +quantity && rent?.value && proxyType?.value;

    if (proxyType?.value === PROXY_TYPES.mobile.code) {
      return baseConditions && mobileOperator?.value && modemRotationMin?.value >= 0;
    }

    return baseConditions;
  };

  useEffect(() => {
    if (!isEnoughDataForPriceCalculation()) {
      setPrice(null);
      return;
    }

    const params = {
      projectId: asyncGoals?.value,
      countryId: country?.id,
      paymentSystemId: payment?.id,
      count: +quantity,
      rentPeriodId: rent?.id,
      proxyType: proxyType?.value,
      mobileOperatorTag: mobileOperator?.value,
      rotationMin: modemRotationMin?.value
    };

    // eslint-disable-next-line no-empty-function
    calculatePrice(setPrice, params, lang, () => {}, {});
    // eslint-disable-next-line
  }, [quantity, proxyType?.value, country?.id, rent?.id, asyncGoals?.value, auth?.value, mobileOperator?.value, modemRotationMin?.value, payment?.id]);

  const getCountryLabel = () => {
    if (!country) return "-";

    const code = country?.code || country?.countryCode;
    const enName = country.enName || country?.countryEnName;
    const ruName = country.name || country?.countryName;

    const countryName = lang === "en" ? enName : ruName;
    const countryFlag = getCountryFlag(code);

    return (<>{countryFlag} {countryName}</>);
  };

  return (
    <div className="new-order__preview">
      <div className="new-order__preview-wrapper">
        <div className="new-order__title">{preview}</div>
        <div className="new-order__preview-container">
          <div className="new-order__label">{proxyTypeLabel}</div>
          <div className="new-order__value">{proxyType?.label ? `${proxyType?.label}` : "-"} </div>
        </div>
        <div className="new-order__preview-container">
          <div className="new-order__label">{location}</div>
          <div className="new-order__value new-order__value--country">{getCountryLabel()}</div>
        </div>
        {proxyType?.value === PROXY_TYPES.mobile.code && (
          <>
            <div className="new-order__preview-container">
              <div className="new-order__label">{operator}</div>
              <div className="new-order__value">{mobileOperator?.operatorName || "-"}</div>
            </div>
            <div className="new-order__preview-container">
              <div className="new-order__label">{rotation}</div>
              <div className="new-order__value">{modemRotationMin?.label || "-"}</div>
            </div>
          </>
        )}
        <div className="new-order__preview-container">
          <div className="new-order__label">{priceByOne}</div>
          <div className="new-order__value">{price ? `$${price?.usdPrice}` : "-"}</div>
        </div>
        <div className="new-order__preview-container">
          <div className="new-order__label">{quantityLabel}</div>
          <div className="new-order__value">{quantity ? `${quantity} IPs` : "-"}</div>
        </div>
        <div className="new-order__preview-container">
          <div className="new-order__label">{period}</div>
          <div className="new-order__value">{rent?.label ? `${rent?.label}` : "-"}</div>
        </div>
        <Divider/>
        <div className="new-order__preview-container">
          <div className="new-order__label">{total}</div>
          <div className="new-order__total">{price ? `$${price?.usdAmount}` : "-"}</div>
        </div>
        <CfCaptcha ref={ref} setCfToken={setCfToken}/>
        <AgreeWithRules
          handler={() => setIsAgreeWithConditions((current) => !current)}
        />
        <Button tab="confirm">
          {isLoading ? <ButtonLoader /> : confirmOrder}
        </Button>
      </div>
    </div>
  );
});
